import sharedContactDB from 'libs/DB/sharedContactDB'
import { contactClient } from 'myNet'
import { EventEmitter } from 'events'
import { isEmail } from 'utils/ui'

// ──────────────────────────────────────
// Types
// ──────────────────────────────────────

interface ContactRequestOptions {
  headers: {
    'Workspace-Id': string
    'user-email': string
  }
}

interface ContactPhoto {
  url?: string
}

interface ContactPerson {
  display_name?: string
  [key: string]: unknown
}

interface ContactItem {
  id?: string
  avatarUrl?: string
  photo?: ContactPhoto | null
  person?: ContactPerson | null
  [key: string]: unknown
}

interface ContactListParams {
  offset?: number
  limit?: number
  brief?: boolean
  email?: string | string[]
  phone?: string | string[]
  keyword?: string
  category_id?: string | number
  deleted?: boolean
  all_provider?: boolean
  [key: string]: unknown
}

interface ContactListResponse {
  data: ContactItem[]
}

interface CategoryParams {
  id?: string | number
  name?: string
  color?: string
  [key: string]: unknown
}

interface CachedItem {
  data: ContactItem | null
  isExpired: () => boolean
}

type PendingResolver = (value: ContactItem | null) => void

// ──────────────────────────────────────
// ContactHelper
// ──────────────────────────────────────

// contactHelper에서는 contact정보를 Group과 list로 나눠서 관리한다.
// 데이터는 DB에 캐시로 저장하고, 만료 시간은 기본 2일로 설정한다.
// list 정보와 group 정보는 어디에서나 사용할 수 있도록 helper로 구현한다.
class ContactHelper {
  workspaceId = ''
  email = ''
  findPromiseMap: Record<string, PendingResolver[] | null> = {}
  errorExpireDuration = 1000 * 60 * 30 // 30분 - 오류 발생 시 캐시 유지 시간

  private contactEmitter: EventEmitter

  constructor() {
    this.contactEmitter = new EventEmitter()
  }

  initHelper = async (workspaceId: string, email: string): Promise<void> => {
    this.workspaceId = workspaceId
    this.email = email
  }

  private getOptions = (): ContactRequestOptions => ({
    headers: {
      'Workspace-Id': this.workspaceId,
      'user-email': this.email,
    },
  })

  // ── API 메서드 ──

  getHistory = async (params: { id: string }) => {
    return contactClient.get(`/history/${params.id}`, { all_provider: true }, this.getOptions())
  }

  contactCounts = async (params: ContactListParams = {}) => {
    return contactClient.get('/contact/counts', params, this.getOptions())
  }

  category = async (params: Record<string, unknown> = {}) => {
    return contactClient.get('/category', params, this.getOptions())
  }

  createCategory = async (params: CategoryParams) => {
    const res = contactClient.post('/category', params, this.getOptions())
    this.fireContactChange()
    return res
  }

  updateCategory = async (params: CategoryParams) => {
    const res = contactClient.put(`/category/${params.id}`, params, this.getOptions())
    this.fireContactChange()
    return res
  }

  deleteCategory = async (id: string | number) => {
    const res = contactClient.delete(`/category/${id}`, null, this.getOptions())
    this.fireContactChange()
    return res
  }

  createContact = async (params: object, photoFile?: File) => {
    const formData = new FormData()
    const data = { ...params } as Record<string, unknown>

    // photoFile이 있으면 upload_file 파트로 전송하고 data.photo는 제거한다.
    // photoFile이 없으면 data.photo를 그대로 전달한다. 단 값이 null이면 불필요하므로 제거한다.
    if (photoFile) {
      delete data.photo
      formData.append('upload_file', photoFile)
    } else if (data.photo === null) {
      delete data.photo
    }
    formData.append('data', JSON.stringify(data))

    const res = contactClient.postFormData('/contact', formData, this.getOptions())
    this.fireContactChange()
    return res
  }

  updateContact = async (
    params: { id: string } & object,
    photoFile?: File,
    photoDelete = false,
  ) => {
    const formData = new FormData()
    const data = { ...params } as Record<string, unknown>

    if (photoDelete) {
      data.photo = null
    }
    formData.append('data', JSON.stringify(data))
    if (photoFile) {
      formData.append('upload_file', photoFile)
    }

    const res = contactClient.putFormData(`/contact/${params.id}`, formData, this.getOptions())
    this.fireContactChange()
    return res
  }

  mergeContact = async (id: string, params: object, photoFile?: File) => {
    const formData = new FormData()
    formData.append('data', JSON.stringify(params))
    if (photoFile) {
      formData.append('upload_file', photoFile)
    }

    const res = contactClient.patchFormData(`/contact/${id}`, formData, this.getOptions())
    this.fireContactChange()
    return res
  }

  deleteContact = async (id: string, isPermanent = false, emptyTrash = false) => {
    let url: string
    if (isPermanent) {
      url = `/contact/${id}?actually=true`
    } else if (emptyTrash) {
      url = '/contact/empty'
    } else {
      url = `/contact/${id}`
    }

    const res = contactClient.delete(url, null, this.getOptions())
    this.fireContactChange()
    return res
  }

  restoreContact = async (id: string) => {
    const res = contactClient.post(`/contact/restore/${id}`, null, this.getOptions())
    this.fireContactChange()
    return res
  }

  setFavorite = async (id: string, favorite: boolean) => {
    const res = favorite
      ? contactClient.post(`/favorite/${id}`, null, this.getOptions())
      : contactClient.delete(`/favorite/${id}`, null, this.getOptions())
    this.fireContactChange()
    return res
  }

  getContact = async (id: string | number, deleted?: boolean) => {
    try {
      return await contactClient.get(
        `/contact/${id}`,
        { deleted: deleted === true },
        this.getOptions(),
      )
    } catch (err) {
      console.error(' -- getContact error : ', err)
      return null
    }
  }

  contactList = async (params: ContactListParams) => {
    try {
      return await contactClient.get('/contact', params, this.getOptions())
    } catch (err) {
      console.error(' -- contactList error : ', err)
      return null
    }
  }

  favoriteList = async (params: ContactListParams) => {
    try {
      return await contactClient.get('/favorite', params, this.getOptions())
    } catch (err) {
      console.error(' -- favoriteList error : ', err)
      return null
    }
  }

  findDuplicatedContacts = async (emails?: string[], phones?: string[]): Promise<ContactItem[]> => {
    const params: ContactListParams = {
      offset: 0,
      limit: 30,
      brief: true,
    }
    if (emails?.length) {
      params.email = emails
    }
    if (phones?.length) {
      params.phone = phones
    }

    const res = await contactClient.get<ContactListResponse>('/contact', params, this.getOptions())
    return res.data.data
  }

  getAvatarUrl = (originData: ContactItem): string | null => {
    const { avatarUrl, photo } = originData

    // photo.url(사용자 업로드 사진)이 avatarUrl(외부 프로바이더)보다 우선한다.
    // 두 값이 모두 존재하면 photo.url을 반환해야 하므로 avatarUrl을 기본값으로 설정한 뒤
    // photo.url이 있으면 덮어쓴다.
    let imgSrc: string | null = null
    if (avatarUrl != null && avatarUrl.trim().length > 0) {
      imgSrc = avatarUrl
    }
    if (photo != null && photo.url != null) {
      imgSrc = photo.url
    }
    return imgSrc
  }

  getAvatarUrlByEmail = async (email: string): Promise<string | null> => {
    const userData = await this.findContact(email)
    return userData != null ? this.getAvatarUrl(userData) : null
  }

  // ── 변경 이벤트 ──

  addContactChangeListener = (callback: () => void): void => {
    this.contactEmitter.on('contact', callback)
  }

  removeContactChangeListener = (callback: () => void): void => {
    this.contactEmitter.off('contact', callback)
  }

  // 연속된 mutation(create → setFavorite 등)에서 각각의 이벤트가 개별로 발생해야
  // 리스너가 매번 갱신할 수 있으므로, debounce하지 않고 호출마다 독립 timer를 생성한다.
  fireContactChange = (): void => {
    setTimeout(() => {
      this.contactEmitter.emit('contact')
    }, 100)
  }

  // ── DB 캐시 조회 (이메일/전화번호) ──

  findContact = (email: string, forceReload?: boolean): Promise<ContactItem | null> => {
    return this.findByKey('email', email, forceReload)
  }

  findContactByPhone = (phone: string, forceReload?: boolean): Promise<ContactItem | null> => {
    return this.findByKey('phone', phone, forceReload)
  }

  /**
   * findContact / findContactByPhone 공통 로직.
   * 1) sharedContactDB 캐시 조회 → 유효하면 즉시 반환
   * 2) 동일 키로 진행 중인 요청이 있으면 대기 큐에 추가 (중복 API 호출 방지)
   * 3) 없으면 contactList API 호출 → 결과를 DB에 캐시 → 대기 큐 전체 resolve
   */
  private findByKey = (
    keyType: 'email' | 'phone',
    keyValue: string,
    forceReload?: boolean,
  ): Promise<ContactItem | null> => {
    return new Promise((resolve) => {
      // keyValue가 비어있거나(공백 포함) email 키인데 유효한 이메일이 아닌 경우
      // 서버에 요청하면 422(Unprocessable Content)로 거절되므로 즉시 null 반환.
      const trimmed = typeof keyValue === 'string' ? keyValue.trim() : ''
      if (trimmed.length === 0 || (keyType === 'email' && isEmail(trimmed) === false)) {
        resolve(null)
        return
      }

      const resolvePending = (data: ContactItem | null): void => {
        const pendingList = this.findPromiseMap[keyValue]
        if (pendingList != null) {
          pendingList.forEach((resolveFunc) => resolveFunc(data))
          this.findPromiseMap[keyValue] = null
        }
      }

      const onError = (): void => {
        sharedContactDB
          .putData(keyValue, null, this.errorExpireDuration)
          .then(() => resolvePending(null))
          .catch(() => resolvePending(null))
      }

      const onRecv = (res: { data: ContactListResponse } | null): void => {
        if (res?.data?.data?.length) {
          const contactData = res.data.data[0]
          sharedContactDB
            .putData(keyValue, contactData)
            .then(() => resolvePending(contactData))
            .catch(() => resolvePending(contactData))
        } else {
          onError()
        }
      }

      const startFetch = (): void => {
        if (this.findPromiseMap[keyValue] == null) {
          this.findPromiseMap[keyValue] = [resolve]
          this.contactList({ [keyType]: keyValue, brief: false })
            .then((res) => onRecv(res as { data: ContactListResponse } | null))
            .catch((err: unknown) => {
              console.error(` -- findByKey(${keyType}) error : `, err)
              onError()
            })
        } else {
          this.findPromiseMap[keyValue]!.push(resolve)
        }
      }

      sharedContactDB
        .getData(keyValue)
        .then((existItem: CachedItem | null) => {
          if (forceReload !== true && existItem != null && existItem.isExpired() === false) {
            resolve(existItem.data)
            return
          }
          startFetch()
        })
        .catch((err: unknown) => {
          console.log(' - sharedContactDB.getData :', err)
          startFetch()
        })
    })
  }
}

const contactHelper = new ContactHelper()

export default contactHelper
